import {
  SOCIAL_HISTORY_EXCLUSIVE,
  SOCIAL_HISTORY_GROUPS,
  SOCIAL_HISTORY_SUB_FIELDS,
  SOCIAL_SUB_NONE_VALUE,
  SocialSubField,
  SocialSubFieldGroup,
} from "../readmission-f2f-social-constant";
import {
  ChooseOneValue,
  GroupValue,
  SocialHistoryQuestion,
  SummaryLine,
} from "../readmission-f2f-types";
import { emptyChooseOneValue, isGroupValue } from "../readmission-f2f-utils";
import {
  buildGroupEntries,
  toNarrativeCase,
} from "../readmission-f2f-summary-utils";
import { useSocialHistorySection } from "./use-social-history-section";

export interface UseSocialHistoryFields extends ReturnType<
  typeof useSocialHistorySection
> {
  ordered: SocialHistoryQuestion[];
  summaryLines: SummaryLine[];
  orderedOptions: (q: SocialHistoryQuestion) => string[];
  exclusiveOf: (
    q: SocialHistoryQuestion
  ) => Record<string, string[]> | undefined;
  chooseOneOf: (q?: SocialHistoryQuestion) => ChooseOneValue;
  subFieldGroups: (
    q: SocialHistoryQuestion,
    selected: string[]
  ) => SocialSubFieldGroup[];
  subText: (field: SocialSubField) => string;
  subNone: (field: SocialSubField) => boolean;
  setSubText: (field: SocialSubField, text: string) => void;
  setSubNone: (field: SocialSubField, on: boolean) => void;
  clearDeselected: (
    q: SocialHistoryQuestion,
    before: string[],
    after: string[]
  ) => void;
}

/** The social-history questions in template order, their sub-field accessors and the Current Visit lines. */
export const useSocialHistoryFields = (): UseSocialHistoryFields => {
  const section = useSocialHistorySection();
  const { questions, store, setValue } = section;

  const byTitle = new Map(questions.map((q) => [q.title, q]));
  const subQuestionTitles = new Set(
    Object.values(SOCIAL_HISTORY_SUB_FIELDS)
      .flat()
      .flatMap((g) => g.fields)
      .flatMap((f) => [f.question, f.noneQuestion])
      .filter(Boolean)
  );

  // Template order first; questions the template does not know are appended as the API returns them.
  const ordered: SocialHistoryQuestion[] = [
    ...SOCIAL_HISTORY_GROUPS.map((g) => byTitle.get(g.title)).filter(
      (q): q is SocialHistoryQuestion => !!q
    ),
    ...questions.filter(
      (q) =>
        !SOCIAL_HISTORY_GROUPS.some((g) => g.title === q.title) &&
        !subQuestionTitles.has(q.title)
    ),
  ];

  const orderedOptions = (q: SocialHistoryQuestion): string[] => {
    const template = SOCIAL_HISTORY_GROUPS.find((g) => g.title === q.title);
    if (!template) return q.options;
    const known = template.options.filter((o) => q.options.includes(o));
    return [...known, ...q.options.filter((o) => !known.includes(o))];
  };

  const exclusiveOf = (q: SocialHistoryQuestion) => {
    const key = SOCIAL_HISTORY_GROUPS.find((g) => g.title === q.title)?.key;
    return key ? SOCIAL_HISTORY_EXCLUSIVE[key] : undefined;
  };

  const chooseOneOf = (q?: SocialHistoryQuestion): ChooseOneValue => {
    const current = q && store[q.uuid];
    return current && !isGroupValue(current) ? current : emptyChooseOneValue();
  };

  const subText = (field: SocialSubField): string =>
    chooseOneOf(byTitle.get(field.question)).freeText;
  const subNone = (field: SocialSubField): boolean =>
    !!field.noneQuestion &&
    chooseOneOf(byTitle.get(field.noneQuestion)).selected ===
      SOCIAL_SUB_NONE_VALUE;

  const setSubText = (field: SocialSubField, text: string) => {
    const q = byTitle.get(field.question);
    if (q) setValue(q.uuid, { selected: "", freeText: text });
  };
  const setSubNone = (field: SocialSubField, on: boolean) => {
    const q = field.noneQuestion && byTitle.get(field.noneQuestion);
    if (q) {
      setValue(q.uuid, {
        selected: on ? SOCIAL_SUB_NONE_VALUE : "",
        freeText: "",
      });
    }
    if (on) setSubText(field, "");
  };
  const clearSubFields = (group: SocialSubFieldGroup) =>
    group.fields.forEach((field) => {
      setSubText(field, "");
      if (field.noneQuestion) setSubNone(field, false);
    });

  const subNarrative = (group: SocialSubFieldGroup): string =>
    group.fields
      .map((field) => {
        if (subNone(field)) return toNarrativeCase(field.noneQuestion ?? "");
        return subText(field).trim();
      })
      .filter(Boolean)
      .join(", ");

  const subFieldGroups = (q: SocialHistoryQuestion, selected: string[]) =>
    (SOCIAL_HISTORY_SUB_FIELDS[q.title] ?? []).filter((group) =>
      selected.includes(group.option)
    );

  const subDetails = (
    q: SocialHistoryQuestion,
    selected: string[]
  ): Record<string, string> => {
    const details: Record<string, string> = {};
    subFieldGroups(q, selected).forEach((group) => {
      const narrative = subNarrative(group);
      if (narrative) details[group.option] = narrative;
    });
    return details;
  };

  // Sub-field text is folded into the option's bracket so one line covers the group.
  const summaryValue = (q: SocialHistoryQuestion): GroupValue | undefined => {
    const current = store[q.uuid];
    if (!current) return undefined;
    const selected = isGroupValue(current)
      ? current.selected
      : [current.selected].filter(Boolean);
    const details = {
      ...(isGroupValue(current) ? current.details : {}),
      ...subDetails(q, selected),
    };
    return { selected, details, freeText: current.freeText };
  };

  const summaryLines = ordered.flatMap((q) =>
    buildGroupEntries(q.title, summaryValue(q))
  );

  const clearDeselected = (
    q: SocialHistoryQuestion,
    before: string[],
    after: string[]
  ) =>
    (SOCIAL_HISTORY_SUB_FIELDS[q.title] ?? [])
      .filter(
        (group) =>
          before.includes(group.option) && !after.includes(group.option)
      )
      .forEach(clearSubFields);

  return {
    ...section,
    ordered,
    summaryLines,
    orderedOptions,
    exclusiveOf,
    chooseOneOf,
    subFieldGroups,
    subText,
    subNone,
    setSubText,
    setSubNone,
    clearDeselected,
  };
};
